import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import apiClient from '../../../stateManagers/services/axiosInterceptor';

type AccessMap = Record<string, Record<string, boolean>>;

const MODULE_LABELS: Record<string, { label: string; icon: string }> = {
  noticeboard:            { label: 'Noticeboard',            icon: 'pi pi-megaphone'          },
  teachers:               { label: 'Teachers',               icon: 'pi pi-users'              },
  students:               { label: 'Students',               icon: 'pi pi-graduation-cap'     },
  timetable:              { label: 'Timetable',              icon: 'pi pi-calendar'           },
  assigned_teachers:      { label: 'Assigned Teachers',      icon: 'pi pi-user-plus'          },
  events:                 { label: 'Events',                 icon: 'pi pi-star'               },
  exams_calendar:         { label: 'Exams Calendar',         icon: 'pi pi-book'               },
  reset_users_password:   { label: 'Reset User Password',    icon: 'pi pi-lock'               },
  teacher_leave_requests: { label: 'Teacher Leave Requests', icon: 'pi pi-clock'              },
  issues:                 { label: 'Issues',                 icon: 'pi pi-exclamation-circle' },
  fees_management:        { label: 'Fees Management',        icon: 'pi pi-wallet'             },
  expenses:               { label: 'Expenses',               icon: 'pi pi-money-bill'         },
  visitors:               { label: 'Visitors',               icon: 'pi pi-id-card'            },
};

interface StaffData {
  _id: string;
  user_id: string;
  name: string;
  job_profile: string;
  profile_picture_url?: string;
}

interface PanelAccessModalProps {
  open: boolean;
  onClose: () => void;
  staffData: StaffData | null;
}

const PanelAccessModal: React.FC<PanelAccessModalProps> = ({ open, onClose, staffData }) => {
  const toast = useRef<Toast>(null);

  const [modules, setModules] = useState<Record<string, string[]>>({});
  const [access, setAccess] = useState<AccessMap>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (!open || !staffData) {
      console.log('PanelAccessModal: Skipping fetch - open:', open, 'staffData:', staffData);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        console.log('PanelAccessModal: Fetching data for userId:', staffData.user_id);
        
        const [modulesRes, accessRes] = await Promise.all([
          apiClient.get('/role-access-modules'),
          apiClient.get(`/role-access?userId=${staffData.user_id}`),
        ]);

        console.log('Modules response:', modulesRes.data);
        console.log('Access response:', accessRes.data);

        if (modulesRes.data.status === 'success') {
          const fetchedModules: Record<string, string[]> = modulesRes.data.modules;
          setModules(fetchedModules);

          const merged: AccessMap = {};
          for (const [mod, actions] of Object.entries(fetchedModules)) {
            merged[mod] = {};
            for (const a of actions) {
              merged[mod][a] = Boolean(accessRes.data.access?.[mod]?.[a] ?? false);
            }
          }
          setAccess(merged);
        } else {
          toast.current?.show({
            severity: 'error', summary: 'Error',
            detail: modulesRes.data.message || 'Failed to load modules',
            life: 4000,
          });
        }

      } catch (err: any) {
        console.error('PanelAccessModal fetch error:', err);
        toast.current?.show({
          severity: 'error', summary: 'Error',
          detail: err.response?.data?.message || 'Failed to load access settings',
          life: 4000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, staffData]);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask = document.querySelector('.p-dialog-mask');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setAccess({});
      setModules({});
      setLoading(true);
    }, 400);
  };

  const setAction = (mod: string, action: string, value: boolean) => {
    setAccess(prev => ({ ...prev, [mod]: { ...prev[mod], [action]: value } }));
  };

  const grantAll = () => {
    const full: AccessMap = {};
    for (const [mod, actions] of Object.entries(modules)) {
      full[mod] = {};
      for (const a of actions) full[mod][a] = true;
    }
    setAccess(full);
  };

  const revokeAll = () => {
    const empty: AccessMap = {};
    for (const [mod, actions] of Object.entries(modules)) {
      empty[mod] = {};
      for (const a of actions) empty[mod][a] = false;
    }
    setAccess(empty);
  };

  const isAllOn = (mod: string) => modules[mod]?.every(a => !!access[mod]?.[a]) ?? false;
  const isAllOff = (mod: string) => modules[mod]?.every(a => !access[mod]?.[a]) ?? false;
  const isSome = (mod: string) => !isAllOn(mod) && !isAllOff(mod);

  const handleSave = async () => {
    if (!staffData) return;
    setSaving(true);
    try {
      const payload = {
        userId: staffData.user_id,
        access: access,   // ← nest it under "access" to match the Go struct
      };
      
      console.log('Saving access payload:', payload);
      
      const res = await apiClient.post('/role-access', payload);
      
      if (res.data.status !== 'success') {
        throw new Error(res.data.message || 'Failed to save');
      }
      
      toast.current?.show({
        severity: 'success', summary: 'Saved',
        detail: `Panel access updated for ${staffData.name}`,
        life: 3000,
      });
      
      setTimeout(() => handleClose(), 1000);
    } catch (err: any) {
      console.error('Save error:', err);
      toast.current?.show({
        severity: 'error', summary: 'Error',
        detail: err.response?.data?.message || err.message || 'Failed to save',
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
};

  const RadioPill = ({
    label, checked, onChange, color,
  }: {
    label: string;
    checked: boolean;
    onChange: () => void;
    color: { bg: string; text: string; border: string };
  }) => (
    <div
      onClick={onChange}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '5px',
        padding: '4px 12px', borderRadius: '20px', cursor: 'pointer',
        border: `1.5px solid ${checked ? color.border : '#e5e7eb'}`,
        background: checked ? color.bg : 'white',
        color: checked ? color.text : '#9ca3af',
        fontSize: '12px', fontWeight: 600,
        transition: 'all 0.15s', userSelect: 'none',
      }}
    >
      <div style={{
        width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0,
        border: `2px solid ${checked ? color.border : '#d1d5db'}`,
        background: checked ? color.text : 'white',
        transition: 'all 0.15s',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {checked && <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'white' }} />}
      </div>
      {label}
    </div>
  );

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
      <div style={{
        width: '64px', height: '64px', borderRadius: '14px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', flexShrink: 0, border: '3px solid white', overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(37,99,235,0.3)',
      }}>
        {staffData?.profile_picture_url ? (
          <img src={staffData.profile_picture_url} alt={staffData.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <i className="pi pi-shield" style={{ fontSize: '24px' }} />
        )}
      </div>
      <div>
        <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e3a8a', marginBottom: '2px' }}>
          Panel Access — {staffData?.name}
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
          {staffData?.job_profile || 'Staff'} · Configure module permissions
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: '1150px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #eff6ff 100%)',
          maxHeight: 'calc(90vh - 130px)', overflowY: 'auto',
          border: '12px solid rgba(255,255,255,0.8)',
          borderRadius: '0 0 24px 24px', borderTop: 'none',
        }}
        headerStyle={{
          background: 'white', borderBottom: '1px solid #e5e7eb',
          padding: '16px 20px', borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255,255,255,0.8)',
          borderRight: '12px solid rgba(255,255,255,0.8)',
          borderTop: '12px solid rgba(255,255,255,0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal dismissableMask draggable={false} resizable={false} blockScroll
      >
        <style>{`
          .p-dialog-mask { backdrop-filter: blur(4px); background-color: rgba(0,0,0,0.25) !important; animation: panFadeIn 0.35s ease; }
          .p-dialog-mask.mask-closing { animation: panFadeOut 0.4s ease !important; }
          @keyframes panFadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes panFadeOut { from { opacity: 1; } to { opacity: 0; } }
          .p-dialog { animation: panOpen 0.35s cubic-bezier(0.4,0,0.2,1); box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25) !important; }
          .p-dialog.dialog-closing { animation: panClose 0.4s cubic-bezier(0.4,0,0.2,1) !important; }
          @keyframes panOpen { from { opacity: 0; transform: scale(0.92) translateY(-20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
          @keyframes panClose { from { opacity: 1; transform: scale(1) translateY(0); } to { opacity: 0; transform: scale(0.88) translateY(25px); } }
          .p-dialog .p-dialog-content::-webkit-scrollbar { width: 8px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track { background: rgba(239,246,255,0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb { background: rgba(147,197,253,0.6); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover { background: rgba(96,165,250,0.8); }
        `}</style>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', gap: '16px' }}>
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
            <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 600 }}>Loading access settings...</span>
          </div>

        ) : Object.keys(modules).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#ef4444', fontSize: '14px', fontWeight: 600 }}>
            <i className="pi pi-exclamation-triangle" style={{ fontSize: '32px', display: 'block', marginBottom: '12px' }} />
            Failed to load modules. Check /role-access-modules endpoint.
          </div>

        ) : (
          <>
            <div style={{
              background: 'white', borderRadius: '14px', padding: '14px 18px',
              border: '1px solid #dbeafe', marginBottom: '16px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px',
            }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e3a8a', marginBottom: '2px' }}>
                  Module Permissions
                </div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>
                  Use Allow / Block per action, or bulk grant / revoke all
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button onClick={grantAll} style={{
                  padding: '7px 14px', borderRadius: '8px', border: '1px solid #93c5fd',
                  background: '#eff6ff', color: '#1d4ed8', fontSize: '12px', fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px',
                }}>
                  <i className="pi pi-check-circle" style={{ fontSize: '12px' }} /> Grant All
                </button>
                <button onClick={revokeAll} style={{
                  padding: '7px 14px', borderRadius: '8px', border: '1px solid #fca5a5',
                  background: '#fef2f2', color: '#991b1b', fontSize: '12px', fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px',
                }}>
                  <i className="pi pi-times-circle" style={{ fontSize: '12px' }} /> Revoke All
                </button>
                <GlobalButton
                  label={saving ? 'Saving...' : 'Save Access'}
                  onClick={handleSave} disabled={saving} loading={saving}
                  icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                  variant="primary" size="small"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {Object.entries(modules).map(([modKey, actions]) => {
                const meta = MODULE_LABELS[modKey] ?? { label: modKey, icon: 'pi pi-circle' };
                const allOn = isAllOn(modKey);
                const allOff = isAllOff(modKey);
                const some = isSome(modKey);

                return (
                  <div key={modKey} style={{
                    background: 'white', borderRadius: '14px', padding: '16px 20px',
                    border: `1px solid ${allOn ? '#93c5fd' : some ? '#bfdbfe' : '#e5e7eb'}`,
                    transition: 'border-color 0.2s',
                  }}>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '32px', height: '32px', borderRadius: '9px',
                          background: allOn
                            ? 'linear-gradient(135deg, #1d4ed8, #60a5fa)'
                            : some
                            ? 'linear-gradient(135deg, #3b82f6, #93c5fd)'
                            : '#f1f5f9',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'background 0.2s',
                        }}>
                          <i className={meta.icon} style={{ fontSize: '14px', color: allOn || some ? 'white' : '#94a3b8' }} />
                        </div>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>
                          {meta.label}
                        </span>
                        <span style={{
                          fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px',
                          background: allOn ? '#dbeafe' : some ? '#eff6ff' : '#f1f5f9',
                          color: allOn ? '#1d4ed8' : some ? '#2563eb' : '#64748b',
                          border: `1px solid ${allOn ? '#93c5fd' : some ? '#bfdbfe' : '#e2e8f0'}`,
                        }}>
                          {allOn ? 'Full Access' : some ? 'Partial' : 'No Access'}
                        </span>
                      </div>

                      <div style={{ display: 'flex', gap: '6px' }}>
                        <RadioPill
                          label="Allow All"
                          checked={allOn}
                          onChange={() => {
                            const updated: Record<string, boolean> = {};
                            for (const a of actions) updated[a] = true;
                            setAccess(prev => ({ ...prev, [modKey]: updated }));
                          }}
                          color={{ bg: '#eff6ff', text: '#1d4ed8', border: '#93c5fd' }}
                        />
                        <RadioPill
                          label="Block All"
                          checked={allOff}
                          onChange={() => {
                            const updated: Record<string, boolean> = {};
                            for (const a of actions) updated[a] = false;
                            setAccess(prev => ({ ...prev, [modKey]: updated }));
                          }}
                          color={{ bg: '#fef2f2', text: '#991b1b', border: '#fca5a5' }}
                        />
                      </div>
                    </div>

                    <div style={{
                      display: 'flex', flexDirection: 'column', gap: '8px',
                      paddingTop: '12px', borderTop: '1px solid #f1f5f9',
                    }}>
                      {actions.map(action => {
                        const isAllowed = !!access[modKey]?.[action];
                        return (
                          <div key={action} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '8px 14px', borderRadius: '10px',
                            background: isAllowed ? '#f0f9ff' : '#fafafa',
                            border: `1px solid ${isAllowed ? '#bae6fd' : '#f1f5f9'}`,
                            transition: 'all 0.15s',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <i
                                className={isAllowed ? 'pi pi-check-circle' : 'pi pi-circle'}
                                style={{ fontSize: '13px', color: isAllowed ? '#0284c7' : '#cbd5e1' }}
                              />
                              <span style={{
                                fontSize: '13px', fontWeight: 600, textTransform: 'capitalize',
                                color: isAllowed ? '#0c4a6e' : '#94a3b8',
                              }}>
                                {action.replace(/_/g, ' ')}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <RadioPill
                                label="Allow"
                                checked={isAllowed}
                                onChange={() => setAction(modKey, action, true)}
                                color={{ bg: '#eff6ff', text: '#1d4ed8', border: '#93c5fd' }}
                              />
                              <RadioPill
                                label="Block"
                                checked={!isAllowed}
                                onChange={() => setAction(modKey, action, false)}
                                color={{ bg: '#fef2f2', text: '#991b1b', border: '#fca5a5' }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'flex-end' }}>
              <GlobalButton
                label={saving ? 'Saving...' : 'Save Access Settings'}
                onClick={handleSave} disabled={saving} loading={saving}
                icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                variant="primary"
              />
            </div>
          </>
        )}
      </Dialog>
    </>
  );
};

export default PanelAccessModal;